import React, { useCallback, useEffect, useMemo, useRef, useState, lazy, Suspense } from 'react';
import { useParams, useLocation } from 'react-router-dom';
import AlbumDetailView from '../components/AlbumDetailView';
import { useMaestroStore } from '../store/maestroStore';
import { QueueController } from '../transport/QueueController';
import { getCachedFileUrl } from '../lib/playback-bridge';
import { libraryService } from '../services/libraryService';
import { invalidateCachedLocalAlbumDetail } from '../services/albumDetailCache';
import { durationFromTrackRow } from '../utils/trackDuration';
import {
  getCoverArt,
  resolveLocalAlbumCoverDisplay,
  normalizeCoverHash,
  isRemoteCoverViewerShell,
  pickAlbumFolderPath,
} from '../lib/coverArtIPC';
import { getExternalDetailHash, seedIndexCoverHashes } from '../lib/externalAlbumCoverResolve';
import {
  findIndexCoverHintsForAlbum,
  mergeLocalAlbumCoverHints,
  pickCoverHintsFromRow,
} from '../lib/localAlbumIndexCover';
import { pickLocalLibraryDisplayCover } from '../utils/localLibraryCoverPrecedence';
import { isRemoteLibraryViewer, isRemoteConnectorShell } from '../services/libraryService';
import { shouldUseLocalLibraryIpc } from '../services/localLibraryIpcGate';
import { isBadCoverUrl, resolveLocalAlbumCoverSync, pickCoverArtThumbHash } from '../utils/coverArt';
import useAppNavigation from '../hooks/useAppNavigation';
import { trackEvent } from '../hooks/usePersonalization';
import { useLocalEnrichmentPoll } from '../hooks/useLocalEnrichmentPoll';
import { logAlbumNavBreadcrumb, logAlbumNavFailure } from '../utils/albumNavigationDiagnostics';
import { decodeLocalAlbumKey } from '../utils/localAlbumNavigation';
import {
  buildRemoveAlbumConfirmMessage,
  getPerAlbumActionErrorHint,
  logConnectorVersionOnce,
  PER_ALBUM_UPDATE_HINT,
} from '../utils/localAlbumActions';
import { handleGhostAlbumOpen } from '../services/staleAlbumTileReconcile';
import { fetchLocalAlbumDetailWithStaleRecovery } from '../services/staleAlbumOpenRecovery';
import {
  isProvisionalCatalogActionBlocked,
  notifyProvisionalCatalogActionBlocked,
} from '../services/catalogBootSnapshot';
import {
  ALBUM_OPEN_CIRCUIT_BREAKER_MESSAGE,
  isAlbumOpenCircuitOpen,
  recordAlbumOpenFailure,
  recordAlbumOpenSuccess,
  resetAlbumOpenCircuit,
} from '../utils/albumOpenCircuitBreaker';
import { transformLocalAlbum } from '../utils/localAlbumPageTransform';

const MetadataEditorModal = lazy(() => import('../components/library/MetadataEditorModal'));
function fromBase64Url(str) {
  return decodeLocalAlbumKey(str);
}

/** Instant shell from grid navigation state while getAlbum IPC runs. */
function prefillAlbumFromNavState(stateAlbum, albumKey) {
  if (!stateAlbum || !albumKey) return null;
  const raw = stateAlbum.album_key || stateAlbum.id || '';
  if (raw && String(raw) !== String(albumKey)) return null;
  const title = stateAlbum.title || 'Loading…';
  const artistName = stateAlbum.artist_name || stateAlbum.artistName || stateAlbum.artist || 'Unknown Artist';
  const emb = stateAlbum.embedded_cover_hash || null;
  const fol = stateAlbum.folder_cover_hash || null;
  const ext = stateAlbum.external_cover_hash || null;
  const indexHints = findIndexCoverHintsForAlbum(albumKey);
  const mergedHints = mergeLocalAlbumCoverHints(
    { embedded_cover_hash: emb, folder_cover_hash: fol, external_cover_hash: ext },
    pickCoverHintsFromRow(stateAlbum),
    indexHints,
  );
  const localCover = resolveLocalAlbumCoverSync({ ...stateAlbum, ...mergedHints });
  const online = stateAlbum.cover_art_url && !isBadCoverUrl(stateAlbum.cover_art_url) ? stateAlbum.cover_art_url : '';
  const artwork = localCover || online || '';
  return {
    id: albumKey,
    album_key: albumKey,
    title,
    artistName,
    artist: artistName,
    artworkUrl: artwork,
    cover: artwork,
    embedded_cover_hash: mergedHints.embedded_cover_hash ?? null,
    folder_cover_hash: mergedHints.folder_cover_hash ?? null,
    external_cover_hash: mergedHints.external_cover_hash ?? null,
    cover_art_thumb_hash: mergedHints.cover_art_thumb_hash ?? stateAlbum.cover_art_thumb_hash ?? null,
    sample_file_path: mergedHints.sample_file_path ?? stateAlbum.sample_file_path ?? null,
    library_folder_path: mergedHints.library_folder_path ?? stateAlbum.library_folder_path ?? null,
    source: 'local_library',
    __source: 'local_library',
    isLocalLibrary: true,
    tracks: [],
    _prefill: true,
  };
}

/**
 * Shape a local library track into a queue item. Keeps format metadata so the
 * transport layer can pick the correct format_id (lossy / CD / hi-res) and
 * the MiniPlayer can show the real bit-depth/sample-rate.
 */
function buildLocalQueueTrack(t, i, albumData) {
  const rate = t.sample_rate
    || (t.maximum_sampling_rate ? Math.round(Number(t.maximum_sampling_rate) * 1000) : null)
    || albumData?.sample_rate
    || null;
  const depth = t.bit_depth || t.maximum_bit_depth || albumData?.bit_depth || null;
  return {
    id: t.id || `local_${i}`,
    title: t.title || 'Unknown',
    artist: t.artistName || t.artist || albumData?.artistName || '',
    album: albumData?.title || '',
    albumId: albumData?.album_key || albumData?.id || null,
    album_key: albumData?.album_key || albumData?.id || null,
    artworkUrl: t.artworkUrl || albumData?.artworkUrl || '',
    embedded_cover_hash: t.embedded_cover_hash || albumData?.embedded_cover_hash || null,
    folder_cover_hash: t.folder_cover_hash || albumData?.folder_cover_hash || null,
    duration: durationFromTrackRow(t),
    source: 'local_library',
    file_path: t.file_path,
    machine_id: t.machine_id,
    format: String(t.format || albumData?.format || '').toUpperCase(),
    sample_rate: rate,
    bit_depth: depth,
    channels: t.channels ?? null,
  };
}

const GOLD = '#C9A84C';

function LocalManageSection({ albumKey, album, onMetadataSaved, onAlbumRemoved }) {
  const [expanded, setExpanded] = useState(false);
  const [editorOpen, setEditorOpen] = useState(false);
  const [removing, setRemoving] = useState(false);
  const [rereading, setRereading] = useState(false);
  const [repairing, setRepairing] = useState(false);
  const [repairResult, setRepairResult] = useState(null);
  const [capabilityHint, setCapabilityHint] = useState(null);

  const handleReread = async () => {
    logConnectorVersionOnce();
    setCapabilityHint(null);
    setRereading(true);
    try {
      await libraryService.rereadAlbum(albumKey);
      await onMetadataSaved?.();
    } catch (e) {
      console.warn('reread album failed:', e);
      setCapabilityHint(getPerAlbumActionErrorHint(e) || e?.message || 'Re-read failed');
    } finally {
      setRereading(false);
    }
  };

  const handleRemove = async () => {
    if (!confirm(buildRemoveAlbumConfirmMessage(album?.title || album?.album))) return;
    logConnectorVersionOnce();
    setCapabilityHint(null);
    setRemoving(true);
    try {
      await libraryService.removeAlbum(albumKey);
      onAlbumRemoved?.();
    } catch (e) {
      console.warn('remove album failed:', e);
      setCapabilityHint(getPerAlbumActionErrorHint(e) || e?.message || 'Remove failed');
      setRemoving(false);
    }
  };

  const handleRepairFormat = async () => {
    setRepairing(true);
    setRepairResult(null);
    try {
      const resp = await libraryService.repairAlbumFormat(albumKey);
      setRepairResult(resp);
      if (resp?.repaired > 0) onMetadataSaved?.();
    } catch (e) {
      console.warn('repair format failed:', e);
      setRepairResult({ error: e?.message || 'Repair failed' });
    } finally {
      setRepairing(false);
    }
  };

  return (
    <>
      <div className="px-6 md:px-8 pb-3">
        <div className="flex flex-wrap items-center gap-3">
          <button
            onClick={handleReread}
            disabled={rereading}
            className="px-4 py-2 rounded text-sm font-semibold transition flex items-center gap-2"
            style={{ background: 'rgba(201,168,76,0.12)', color: GOLD, border: '1px solid rgba(201,168,76,0.3)', opacity: rereading ? 0.5 : 1 }}
          >
            <svg className={`w-4 h-4 ${rereading ? 'animate-spin' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" /></svg>
            {rereading ? 'Re-reading…' : 'Re-read'}
          </button>
          <button
            onClick={handleRemove}
            disabled={removing}
            className="px-4 py-2 rounded text-sm font-semibold transition flex items-center gap-2"
            style={{ background: 'rgba(239,68,68,0.1)', color: '#ef4444', border: '1px solid rgba(239,68,68,0.3)', opacity: removing ? 0.5 : 1 }}
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" /></svg>
            {removing ? 'Removing…' : 'Remove from library'}
          </button>
        </div>
        {capabilityHint && (
          <p className="mt-2 text-xs" style={{ color: capabilityHint === PER_ALBUM_UPDATE_HINT ? '#fbbf24' : '#ef4444' }}>
            {capabilityHint}
          </p>
        )}
      </div>
      <div className="px-6 md:px-8">
        <div style={{ borderTop: '1px solid var(--border, rgba(255,255,255,0.1))', borderBottom: '1px solid var(--border, rgba(255,255,255,0.1))' }}>
          <button
            onClick={() => setExpanded(!expanded)}
            className="w-full flex items-center justify-between py-3 transition"
            style={{ color: 'var(--text-secondary, rgba(255,255,255,0.5))' }}
          >
            <span className="text-xs font-semibold uppercase tracking-wider">Manage</span>
            <svg
              className="w-4 h-4 transition-transform"
              style={{ transform: expanded ? 'rotate(180deg)' : 'rotate(0deg)' }}
              fill="none" stroke="currentColor" viewBox="0 0 24 24"
            >
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
            </svg>
          </button>
          {expanded && (
            <div className="pb-4 flex flex-col gap-3">
              <div className="flex items-center gap-3">
                <button
                  onClick={() => setEditorOpen(true)}
                  className="px-4 py-2 rounded text-sm font-semibold transition flex items-center gap-2"
                  style={{ background: 'rgba(201,168,76,0.12)', color: GOLD, border: `1px solid rgba(201,168,76,0.3)` }}
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z" /></svg>
                  Edit (Local)
                </button>
                <button
                  onClick={() => setEditorOpen(true)}
                  className="px-4 py-2 rounded text-sm font-semibold transition flex items-center gap-2"
                  style={{ background: 'var(--bg-secondary, rgba(255,255,255,0.08))', color: 'var(--text-primary, #fff)', border: '1px solid var(--border, rgba(255,255,255,0.15))' }}
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" /></svg>
                  Edit Metadata
                </button>
                <button
                  onClick={handleRepairFormat}
                  disabled={repairing}
                  className="px-4 py-2 rounded text-sm font-semibold transition flex items-center gap-2"
                  style={{ background: 'rgba(201,168,76,0.1)', color: GOLD, border: `1px solid rgba(201,168,76,0.3)`, opacity: repairing ? 0.5 : 1 }}
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" /></svg>
                  {repairing ? 'Repairing…' : 'Repair Formats'}
                </button>
              </div>
              {repairResult && (
                <div className="text-xs" style={{ color: repairResult.error ? '#ef4444' : 'rgba(255,255,255,0.5)' }}>
                  {repairResult.error
                    ? repairResult.error
                    : `${repairResult.repaired || 0} of ${repairResult.total || 0} tracks repaired`}
                </div>
              )}
            </div>
          )}
        </div>
      </div>
      {editorOpen && (
        <Suspense fallback={null}>
          <MetadataEditorModal
            albumKey={albumKey}
            album={album}
            onClose={() => setEditorOpen(false)}
            onSaved={() => { setEditorOpen(false); onMetadataSaved?.(); }}
          />
        </Suspense>
      )}
    </>
  );
}

export function LocalAlbumDetailPanel({ albumKeyB64, prefillAlbum: prefillProp }) {
  const location = useLocation();
  const nav = useAppNavigation();
  const { closeLocalAlbum } = nav;
  const albumKey = fromBase64Url(albumKeyB64 || '');

  const navStateAlbumRef = useRef(null);
  const navStateAlbumKeyRef = useRef('');
  const incomingNavAlbum = prefillProp ?? location.state?.album;
  if (incomingNavAlbum && albumKey && navStateAlbumKeyRef.current !== albumKey) {
    navStateAlbumRef.current = incomingNavAlbum;
    navStateAlbumKeyRef.current = albumKey;
  }
  const stateAlbum = navStateAlbumRef.current;

  const fetchHintKey = useMemo(() => {
    if (!stateAlbum) return '';
    return [
      stateAlbum.machine_id || '',
      stateAlbum.title || '',
      stateAlbum.artistName || stateAlbum.artist_name || stateAlbum.artist || '',
      stateAlbum._fromHistory ? '1' : '0',
    ].join('|');
  }, [
    stateAlbum?.machine_id,
    stateAlbum?.title,
    stateAlbum?.artistName,
    stateAlbum?.artist_name,
    stateAlbum?.artist,
    stateAlbum?._fromHistory,
  ]);

  const navPrefill = useMemo(
    () => prefillAlbumFromNavState(stateAlbum, albumKey),
    [stateAlbum, albumKey],
  );
  const [album, setAlbum] = useState(() => navPrefill);
  const [loading, setLoading] = useState(() => !navPrefill);
  const [error, setError] = useState(null);
  const [retryToken, setRetryToken] = useState(0);
  const ghostHandledRef = useRef('');
  const coverFetchFailedRef = useRef(new Set());
  const isPlaying = useMaestroStore((s) => s.playbackState === 'playing' || s.playbackState === 'loading');
  const currentTrack = useMaestroStore((s) => s.currentTrack);

  useEffect(() => {
    if (!albumKey) return;
    const hints = mergeLocalAlbumCoverHints(
      pickCoverHintsFromRow(stateAlbum),
      findIndexCoverHintsForAlbum(albumKey),
    );
    seedIndexCoverHashes(albumKey, hints);
  }, [albumKey, stateAlbum]);

  useEffect(() => {
    if (!albumKey) return;
    console.log('[LocalAlbumPage] mount', {
      albumKey,
      albumKeyB64,
      title: navPrefill?.title || stateAlbum?.title || null,
    });
    logAlbumNavBreadcrumb('route', albumKey, {
      source: 'local_library',
      mechanism: 'search_param',
      title: navPrefill?.title || stateAlbum?.title || null,
    });
  }, [albumKey, albumKeyB64, navPrefill?.title, stateAlbum?.title]);

  useEffect(() => {
    return () => {
      if (albumKey) resetAlbumOpenCircuit(albumKey);
    };
  }, [albumKey]);

  useEffect(() => {
    if (!albumKey) { setError('No album key'); setLoading(false); return; }
    if (isAlbumOpenCircuitOpen(albumKey)) {
      setError(ALBUM_OPEN_CIRCUIT_BREAKER_MESSAGE);
      setLoading(false);
      return;
    }

    let cancelled = false;
    setError(null);
    setLoading(true);

    (async () => {
      try {
        logAlbumNavBreadcrumb('fetch_start', albumKey, { source: 'local_library' });
        const openHints = {
          library_folder_path: stateAlbum?.library_folder_path ?? null,
          sample_file_path: stateAlbum?.sample_file_path ?? null,
          title: stateAlbum?.title ?? null,
          artist: stateAlbum?.artistName
            || stateAlbum?.artist_name
            || stateAlbum?.artist
            || null,
        };
        const resp = shouldUseLocalLibraryIpc()
          ? await fetchLocalAlbumDetailWithStaleRecovery(albumKey, openHints)
          : await libraryService.getAlbum(albumKey, {
            machineId: stateAlbum?.machine_id || undefined,
            title: openHints.title ?? undefined,
            artist: openHints.artist ?? undefined,
          });

        if (cancelled) return;
        if (!resp) {
          const failure = recordAlbumOpenFailure(albumKey);
          if (failure.attempt === 1 && ghostHandledRef.current !== albumKey) {
            ghostHandledRef.current = albumKey;
            queueMicrotask(() => handleGhostAlbumOpen(albumKey));
            setError(null);
            setLoading(true);
            logAlbumNavFailure(albumKey, 'stale id recovery miss — ghost reconcile', { source: 'local_library' });
          } else {
            setError('Album not found');
          }
          return;
        }
        if (resp?.success) {
          recordAlbumOpenSuccess(albumKey);
          const realKey = resp.album?.album_key || albumKey;
          const mergedAlbum = mergeLocalAlbumCoverHints(
            {
              ...resp.album,
              cover_art_thumb_hash: resp.album?.cover_art_thumb_hash ?? stateAlbum?.cover_art_thumb_hash ?? null,
              sample_file_path: resp.album?.sample_file_path ?? stateAlbum?.sample_file_path ?? null,
              library_folder_path: resp.album?.library_folder_path ?? stateAlbum?.library_folder_path ?? null,
            },
            pickCoverHintsFromRow(stateAlbum),
            findIndexCoverHintsForAlbum(realKey),
          );
          const transformed = transformLocalAlbum(mergedAlbum, resp.tracks);
          setAlbum(transformed);

          // Auto-repair: detect format/extension mismatch and fix silently
          const tracks = resp.tracks || [];
          const hasMismatch = tracks.some((t) => {
            if (!t.file_path || !t.format) return false;
            const dot = t.file_path.lastIndexOf('.');
            if (dot < 0) return false;
            const ext = t.file_path.substring(dot + 1).toUpperCase();
            return ext !== (t.format || '').toUpperCase();
          });
          if (hasMismatch) {
            libraryService.repairAlbumFormat(realKey).then((repairResp) => {
              if (cancelled || !repairResp?.repaired) return;
              invalidateCachedLocalAlbumDetail(realKey);
              libraryService.getAlbum(realKey).then((fresh) => {
                if (cancelled || !fresh?.success) return;
                setAlbum(transformLocalAlbum(fresh.album, fresh.tracks));
              }).catch(() => {});
            }).catch(() => {});
          }

          // Legacy client-side enrich — Phase 2 enrichment runs in Connector background worker.
          // Home host never POSTs to cloud enrich for local album keys.
          if (
            !shouldUseLocalLibraryIpc()
            && !resp.album?.cover_art_url
            && !resp.album?.enriched_at
            && !resp.album?.enrichment_attempted_at
          ) {
            libraryService.enrichAlbum(realKey).catch(() => {});
          }
        } else {
          const failure = recordAlbumOpenFailure(albumKey);
          const remoteShell = isRemoteConnectorShell() || isRemoteLibraryViewer();
          const fromHistory = !!stateAlbum?._fromHistory;
          const histMachineId = stateAlbum?.machine_id || null;
          if (failure.breakerOpen) {
            setError(ALBUM_OPEN_CIRCUIT_BREAKER_MESSAGE);
          } else if (remoteShell) {
            setError(
              'Your home library is offline or unreachable. Open Maestro on the Mac that stores your music, go to Settings → enable Home Library Access, then try again.',
            );
          } else if (fromHistory) {
            setError(
              histMachineId
                ? 'This album was played on another device and is not in your library here. Open Maestro on the computer where you played it, or add the album to this library.'
                : 'This album is not in your library on this device. It may have been removed or played from another source.',
            );
          } else {
            setError('Album not found');
            if (failure.attempt === 1 && ghostHandledRef.current !== albumKey) {
              ghostHandledRef.current = albumKey;
              queueMicrotask(() => handleGhostAlbumOpen(albumKey));
            }
          }
          logAlbumNavFailure(albumKey, 'getAlbum returned no data', { source: 'local_library' });
        }
      } catch (e) {
        if (!cancelled) {
          if (isProvisionalCatalogActionBlocked(e)) {
            notifyProvisionalCatalogActionBlocked(e);
            setError('Your library is finishing loading — try again in a moment');
            logAlbumNavFailure(albumKey, 'provisional_catalog_action_blocked', { source: 'local_library' });
          } else {
            const failure = recordAlbumOpenFailure(albumKey);
            const notFound = String(e?.message || '').match(/^album not found for id '/i);
            if (notFound && failure.attempt === 1 && ghostHandledRef.current !== albumKey) {
              ghostHandledRef.current = albumKey;
              queueMicrotask(() => handleGhostAlbumOpen(albumKey));
              setError(null);
              setLoading(true);
              logAlbumNavFailure(albumKey, 'getAlbum threw stale id — ghost recovery', { source: 'local_library' });
            } else {
              setError(
                failure.breakerOpen
                  ? ALBUM_OPEN_CIRCUIT_BREAKER_MESSAGE
                  : (e?.message || 'Failed to load album'),
              );
              logAlbumNavFailure(albumKey, e?.message || 'getAlbum threw', { source: 'local_library' });
            }
          }
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();

    return () => { cancelled = true; };
  }, [albumKey, fetchHintKey, retryToken]);

  useEffect(() => {
    if (!album || album._prefill) return;
    logAlbumNavBreadcrumb('render', albumKey, {
      source: 'local_library',
      title: album.title || null,
    });
  }, [albumKey, album?.id, album?.title, album?._prefill]);

  useEffect(() => {
    if (!album) return;
    let cancelled = false;
    const embHash = normalizeCoverHash(album.embedded_cover_hash);
    const folHash = normalizeCoverHash(album.folder_cover_hash);
    const thumbHash = pickCoverArtThumbHash(album);
    const bareAlbumKey = album.album_key || album.id || '';
    const detailHash = normalizeCoverHash(getExternalDetailHash(bareAlbumKey));
    const primaryEmbHash = embHash || detailHash;
    const folderPath = pickAlbumFolderPath(album);
    const hasSyncUrl = resolveLocalAlbumCoverSync(album);
    if (hasSyncUrl || (!thumbHash && !primaryEmbHash && !folHash && !folderPath)) return;
    const failKey = `${thumbHash || ''}|${primaryEmbHash || ''}|${folHash || ''}`;
    if (coverFetchFailedRef.current.has(failKey)) return;
    (async () => {
      const blobUrl = await resolveLocalAlbumCoverDisplay({
        primaryEmbeddedHash: primaryEmbHash,
        folderHash: folHash,
        externalHash: album?.external_cover_hash,
        album,
      });
      if (cancelled) return;
      if (!blobUrl) {
        coverFetchFailedRef.current.add(failKey);
        return;
      }
      setAlbum((prev) => {
        if (!prev) return prev;
        return { ...prev, artworkUrl: blobUrl, cover: blobUrl };
      });
    })();
    return () => { cancelled = true; };
  }, [album?.embedded_cover_hash, album?.folder_cover_hash, album?.cover_art_thumb_hash, album?.sample_file_path, album?.library_folder_path, album?.album_key, album?.id]);

  useEffect(() => {
    if (album) {
      trackEvent('album_viewed', {
        entityType: 'album',
        entityId: albumKey,
        entityName: album.title,
        metadata: { artistName: album.artistName, source: 'local_library' },
      });
    }
  }, [albumKey, album?.title]);

  const handleTrackPlay = useCallback((albumData, trackIndex) => {
    const tracks = albumData?.tracks || [];
    if (!tracks.length) return;
    const formattedTracks = tracks.map((t, i) => buildLocalQueueTrack(t, i, albumData));
    QueueController.playFrom(formattedTracks, trackIndex);
  }, []);

  const handlePlayNext = useCallback((tracks, albumData) => {
    const store = useMaestroStore.getState();
    const items = (tracks || []).map((t, i) => ({
      track: buildLocalQueueTrack(t, i, albumData),
    }));
    store.playNext(items);
  }, []);

  const handleAddToQueue = useCallback((tracks, albumData) => {
    const store = useMaestroStore.getState();
    const items = (tracks || []).map((t, i) => ({
      track: buildLocalQueueTrack(t, i, albumData),
    }));
    store.addToQueue(items);
  }, []);

  const handlePlayOnlyThisTrack = useCallback((t, albumData, displayTracks) => {
    const tracks = displayTracks?.length ? displayTracks : (albumData?.tracks || []);
    if (!tracks.length || !t) return;
    const trackIndex = tracks.findIndex((dt) => (dt.id || dt.serviceTrackId) === (t.id || t.serviceTrackId));
    const sourceTrack = trackIndex >= 0 ? tracks[trackIndex] : t;
    const i = trackIndex >= 0 ? trackIndex : 0;
    QueueController.playSingle(buildLocalQueueTrack(sourceTrack, i, albumData));
  }, []);

  const refreshAlbum = useCallback(async () => {
    if (!albumKey) return null;
    const machineId = stateAlbum?.machine_id || album?.machine_id || undefined;
    try {
      const resp = await libraryService.getAlbum(albumKey, { machineId });
      if (resp?.success) {
        setAlbum(transformLocalAlbum(resp.album, resp.tracks));
        return resp.album;
      }
    } catch {}
    return null;
  }, [albumKey, album?.machine_id, stateAlbum?.machine_id]);

  useLocalEnrichmentPoll({
    enabled: !!album && !album.enrichment_attempted_at,
    fetchFn: refreshAlbum,
    onUpdate: () => {},
  });

  if (loading && !album) {
    return (
      <div className="flex items-center justify-center min-h-[50vh]">
        <div className="w-10 h-10 border-2 border-white/20 border-t-amber-500 rounded-full animate-spin" />
      </div>
    );
  }

  if (error || !album) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[50vh] gap-4 px-6 text-center">
        <p className="text-white/50">{error || 'Album not found'}</p>
        <div className="flex gap-3">
          <button
            onClick={() => {
              resetAlbumOpenCircuit(albumKey);
              ghostHandledRef.current = '';
              setRetryToken((t) => t + 1);
            }}
            className="px-4 py-2 rounded-xl text-sm font-semibold border border-white/20 text-white/80"
          >
            Retry
          </button>
          <button onClick={() => closeLocalAlbum()} className="px-4 py-2 rounded-xl text-sm font-semibold" style={{ background: '#C9A84C', color: '#1a1a2e' }}>
            Back to Library
          </button>
        </div>
      </div>
    );
  }

  return (
    <AlbumDetailView
      album={album}
      tracks={album.tracks || []}
      onTrackPlay={handleTrackPlay}
      onPlayNext={handlePlayNext}
      onPlayOnlyThisTrack={handlePlayOnlyThisTrack}
      onAddToQueue={handleAddToQueue}
      onArtistClick={(id, name) => nav.openArtist({ id, name, __source: 'local_library' })}
      onPersonClick={(id, name) => nav.openPerson(id, name)}
      onBioLinkClick={() => {}}
      onAlbumClick={(a) => nav.openAlbum(a)}
      onGenreClick={(k) => nav.openGenre(k)}
      isPlaying={isPlaying}
      currentTrackId={currentTrack?.id || null}
      selectedTrackIndex={null}
      onTrackSelect={() => {}}
      adminSection={
        <LocalManageSection
          albumKey={albumKey}
          album={album}
          onMetadataSaved={refreshAlbum}
          onAlbumRemoved={() => closeLocalAlbum()}
        />
      }
    />
  );
}

export { default as LocalAlbumLegacyRedirect } from './LocalAlbumLegacyRedirect';
